/**
 * InputBar - Pill-shaped text/number input
 *
 * Core component: No internal dependencies.
 * Used for: amount inputs, percentage inputs, search inputs, etc.
 */

import { cn } from '@/lib/utils';

export interface InputBarProps {
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Input type */
  type?: 'text' | 'number';
  /** Suffix text (e.g., "%", "₼") */
  suffix?: string;
  /** Prefix text (e.g., "$", "₼") */
  prefix?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Size variant (affects padding/font) */
  size?: 'sm' | 'md' | 'lg';
  /** Width variant */
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  /** Additional className for container */
  className?: string;
  /** Min value (for number type) */
  min?: number;
  /** Max value (for number type) */
  max?: number;
}

export function InputBar({
  value,
  onChange,
  placeholder = '',
  type = 'text',
  suffix,
  prefix,
  disabled = false,
  align = 'center',
  size = 'md',
  width = 'full',
  className,
  min,
  max,
}: InputBarProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // For number type, validate
    if (type === 'number') {
      // Allow empty string
      if (newValue === '') {
        onChange(newValue);
        return;
      }

      // Only allow digits
      if (!/^\d*$/.test(newValue)) {
        return;
      }

      const numValue = parseInt(newValue, 10);

      // Check max
      if (max !== undefined && numValue > max) {
        return;
      }
    }

    onChange(newValue);
  };

  const containerSizeStyles = {
    sm: 'py-1.5 px-2.5',
    md: 'py-2 px-3',
    lg: 'py-2.5 px-4',
  };

  const fontSizeStyles = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const widthValues = {
    xs: 63,
    sm: 81,
    md: 100,
    lg: 125,
    full: 175,
  };

  const alignStyles = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div
      style={{ width: widthValues[width], maxWidth: widthValues[width], flexShrink: 0 }}
      className={cn(
        'flex items-center rounded-full bg-white',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'border border-slate-200',
        'focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-100',
        'transition-all duration-200',
        containerSizeStyles[size],
        disabled && 'opacity-50',
        className
      )}
    >
      {prefix && (
        <span className="text-slate-400 font-medium mr-1 text-sm">{prefix}</span>
      )}
      <input
        type="text"
        inputMode={type === 'number' ? 'numeric' : 'text'}
        pattern={type === 'number' ? '[0-9]*' : undefined}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        min={min}
        max={max}
        className={cn(
          'flex-1 min-w-0 font-semibold text-slate-800',
          'bg-transparent border-none outline-none',
          'placeholder:text-slate-300',
          fontSizeStyles[size],
          alignStyles[align]
        )}
      />
      {suffix && (
        <span className="text-slate-400 font-medium ml-1 text-sm">{suffix}</span>
      )}
    </div>
  );
}
